import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  Loader2,
  Crown,
  Shield,
  User,
  Building2,
  UserCircle,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { GlobalLoader } from "@/components/ui/global-loader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useInvitePreview, useAcceptInvite } from "@/lib/api/teams";
import { signIn, sessionQueryOptions } from "@/lib/auth";
import { useSuspenseQuery } from "@tanstack/react-query";

function InviteHeader() {
  return (
    <header className="border-border/40 border-b">
      <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-4">
        <img src="/BLDR.png" alt="BLDR" className="size-8 rounded-lg" />
        <span className="text-lg font-bold text-rose-500 dark:text-rose-400">
          BLDR
        </span>
        <span className="text-border text-lg font-light">/</span>
        <span className="text-foreground text-sm font-medium">Team Invite</span>
      </div>
    </header>
  );
}

export const Route = createFileRoute("/invite/$token")({
  component: InviteAcceptPage,
});

const roleConfig = {
  owner: {
    label: "Owner",
    icon: Crown,
    description: "Full access to all team settings",
  },
  admin: {
    label: "Admin",
    icon: Shield,
    description: "Can manage members and invitations",
  },
  member: { label: "Member", icon: User, description: "Basic team access" },
};

function InviteAcceptPage() {
  usePageTitle("Invite");

  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } =
    useSuspenseQuery(sessionQueryOptions);
  const {
    data: previewData,
    isPending: previewPending,
    error: previewError,
  } = useInvitePreview(token);
  const acceptInvite = useAcceptInvite();

  const handleSignIn = () => {
    signIn.social({
      provider: "discord",
      callbackURL: window.location.href,
    });
  };

  const handleAccept = () => {
    acceptInvite.mutate(token, {
      onSuccess: () => {
        navigate({ to: "/dashboard" });
      },
    });
  };

  const handleDecline = () => {
    navigate({ to: "/" });
  };

  if (sessionPending || previewPending) {
    return (
      <div className="bg-background flex min-h-dvh flex-col">
        <InviteHeader />
        <GlobalLoader />
      </div>
    );
  }

  if (previewError || !previewData?.data?.invite) {
    const errorMessage =
      previewError instanceof Error
        ? previewError.message
        : "This invitation is invalid or has expired.";

    return (
      <div className="bg-background flex min-h-dvh flex-col">
        <InviteHeader />
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="bg-destructive/10 mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
                <AlertCircle className="text-destructive size-6" />
              </div>
              <CardTitle>Invalid invitation</CardTitle>
              <CardDescription>{errorMessage}</CardDescription>
            </CardHeader>
            <CardFooter className="justify-center">
              <Button variant="outline" onClick={() => navigate({ to: "/" })}>
                Go to home
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  const invite = previewData.data.invite;
  const role = roleConfig[invite.role];
  const RoleIcon = role.icon;
  const isPersonal = invite.tenantType === "personal";

  if (!session?.user) {
    return (
      <div className="bg-background flex min-h-dvh flex-col">
        <InviteHeader />
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4">
                <Avatar className="size-16">
                  <AvatarFallback className="bg-muted">
                    {isPersonal ? (
                      <UserCircle className="text-muted-foreground size-8" />
                    ) : (
                      <Building2 className="text-muted-foreground size-8" />
                    )}
                  </AvatarFallback>
                </Avatar>
              </div>
              <CardTitle>Join {invite.tenantName}</CardTitle>
              <CardDescription>
                {invite.createdByUserName} invited you to join as a{" "}
                <Badge variant="secondary" className="mx-1 gap-1">
                  <RoleIcon className="size-3" />
                  {role.label}
                </Badge>
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-muted-foreground text-sm">
                Sign in with Discord to accept this invitation.
              </p>
            </CardContent>
            <CardFooter className="flex-col gap-2">
              <Button onClick={handleSignIn} className="w-full">
                Sign in with Discord
              </Button>
              <Button
                variant="ghost"
                onClick={handleDecline}
                className="w-full"
              >
                Decline
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  if (invite.isAlreadyMember) {
    return (
      <div className="bg-background flex min-h-dvh flex-col">
        <InviteHeader />
        <div className="flex flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle2 className="size-6 text-green-500" />
              </div>
              <CardTitle>Already a member</CardTitle>
              <CardDescription>
                You are already a member of {invite.tenantName}.
              </CardDescription>
            </CardHeader>
            <CardFooter className="justify-center">
              <Button onClick={() => navigate({ to: "/dashboard" })}>
                Go to Dashboard
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background flex min-h-dvh flex-col">
      <InviteHeader />
      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Avatar className="size-16">
                <AvatarFallback className="bg-muted">
                  {isPersonal ? (
                    <UserCircle className="text-muted-foreground size-8" />
                  ) : (
                    <Building2 className="text-muted-foreground size-8" />
                  )}
                </AvatarFallback>
              </Avatar>
            </div>
            <CardTitle>Join {invite.tenantName}</CardTitle>
            <CardDescription>
              {invite.createdByUserName} invited you to join this team.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-muted flex size-10 items-center justify-center rounded-full">
                  <RoleIcon className="text-muted-foreground size-5" />
                </div>
                <div>
                  <p className="font-medium">{role.label}</p>
                  <p className="text-muted-foreground text-xs">
                    {role.description}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex-col gap-2">
            <Button
              onClick={handleAccept}
              className="w-full"
              disabled={acceptInvite.isPending}
            >
              {acceptInvite.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Joining...
                </>
              ) : (
                "Accept invitation"
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={handleDecline}
              className="w-full"
              disabled={acceptInvite.isPending}
            >
              Decline
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
